import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Skeleton,
  useTheme,
  alpha
} from '@mui/material';
import {
  Layers,
  TableRestaurant,
  Receipt,
  AttachMoney
} from '@mui/icons-material';
import { FloorSummary, formatCurrency } from '../../api/invoiceApi';

interface FloorSelectorProps {
  floors: FloorSummary[];
  selectedFloorId: number | null;
  onFloorSelect: (floorId: number | null) => void;
  loading?: boolean;
}

/**
 * FloorSelector - Horizontal scrollable floor selector with summary stats.
 * Allows selecting a floor to filter tables or viewing all floors.
 */
const FloorSelector: React.FC<FloorSelectorProps> = ({
  floors,
  selectedFloorId,
  onFloorSelect,
  loading = false
}) => {
  const theme = useTheme();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', pb: 1 }}>
        {[1, 2, 3].map((i) => (
          <Skeleton
            key={i}
            variant="rounded"
            width={180}
            height={80}
            sx={{ flexShrink: 0 }}
          />
        ))}
      </Box>
    );
  }

  // Calculate totals for "All Floors" option
  const totals = floors.reduce(
    (acc, floor) => ({
      tableCount: acc.tableCount + floor.tableCount,
      activeSessionCount: acc.activeSessionCount + floor.activeSessionCount,
      totalRevenue: acc.totalRevenue + floor.totalRevenue,
      totalSessionCount: acc.totalSessionCount + floor.totalSessionCount
    }),
    { tableCount: 0, activeSessionCount: 0, totalRevenue: 0, totalSessionCount: 0 }
  );

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 1.5,
        overflowX: 'auto',
        pb: 1,
        mb: 2,
        '&::-webkit-scrollbar': {
          height: 6
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: alpha(theme.palette.text.primary, 0.2),
          borderRadius: 3
        }
      }}
    >
      {/* All Floors Option */}
      <FloorCard
        id={null}
        name="All Floors"
        floorNumber={null}
        tableCount={totals.tableCount}
        activeSessionCount={totals.activeSessionCount}
        totalRevenue={totals.totalRevenue}
        totalSessionCount={totals.totalSessionCount}
        isSelected={selectedFloorId === null}
        onClick={() => onFloorSelect(null)}
        isAllFloors
      />

      {/* Individual Floor Options */}
      {floors.map((floor) => (
        <FloorCard
          key={floor.id}
          id={floor.id}
          name={floor.name}
          floorNumber={floor.floorNumber}
          tableCount={floor.tableCount}
          activeSessionCount={floor.activeSessionCount}
          totalRevenue={floor.totalRevenue}
          totalSessionCount={floor.totalSessionCount}
          isSelected={selectedFloorId === floor.id}
          onClick={() => onFloorSelect(floor.id)}
        />
      ))}
    </Box>
  );
};

interface FloorCardProps {
  id: number | null;
  name: string;
  floorNumber: number | null;
  tableCount: number;
  activeSessionCount: number;
  totalRevenue: number;
  totalSessionCount: number;
  isSelected: boolean;
  onClick: () => void;
  isAllFloors?: boolean;
}

const FloorCard: React.FC<FloorCardProps> = ({
  name,
  floorNumber,
  tableCount,
  activeSessionCount,
  totalRevenue,
  totalSessionCount,
  isSelected,
  onClick,
  isAllFloors = false
}) => {
  const theme = useTheme();

  return (
    <Paper
      elevation={isSelected ? 3 : 0}
      onClick={onClick}
      sx={{
        p: 1.5,
        minWidth: 160,
        flexShrink: 0,
        cursor: 'pointer',
        borderRadius: 2,
        border: isSelected
          ? `2px solid ${theme.palette.primary.main}`
          : `1px solid ${theme.palette.divider}`,
        backgroundColor: isSelected
          ? alpha(theme.palette.primary.main, 0.05)
          : theme.palette.background.paper,
        transition: 'all 0.2s ease',
        '&:hover': {
          borderColor: theme.palette.primary.main,
          transform: 'translateY(-2px)',
          boxShadow: theme.shadows[4]
        }
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        {isAllFloors ? (
          <Layers sx={{ fontSize: 18, color: theme.palette.primary.main }} />
        ) : (
          <Box
            sx={{
              width: 24,
              height: 24,
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
              color: theme.palette.primary.main,
              fontSize: '0.75rem',
              fontWeight: 600
            }}
          >
            {floorNumber ?? 'F'}
          </Box>
        )}
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: isSelected ? 600 : 500,
            color: isSelected ? theme.palette.primary.main : theme.palette.text.primary,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
        >
          {name}
        </Typography>
      </Box>

      {/* Stats */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <TableRestaurant sx={{ fontSize: 14, color: 'text.secondary' }} />
          <Typography variant="caption" color="text.secondary">
            {tableCount} tables
          </Typography>
          {activeSessionCount > 0 && (
            <Chip
              label={`${activeSessionCount} active`}
              size="small"
              color="success"
              sx={{ height: 18, fontSize: '0.65rem', ml: 0.5 }}
            />
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <AttachMoney sx={{ fontSize: 14, color: 'success.main' }} />
          <Typography
            variant="caption"
            sx={{ fontWeight: 600, color: 'success.main' }}
          >
            {formatCurrency(totalRevenue)}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};

export default FloorSelector;
